import { useLocation, useNavigate, useParams } from "react-router";
import Container from "../components/common/Container";
import styled from "styled-components";
import { Badge, Button, Flex, Modal, Select, Table, Tabs } from "@mantine/core";
import EmployeeDetailTable from "../components/EmployeeDetail/Table";
import PageHeader from "../components/common/PageHeader";
import { useEffect, useRef, useState } from "react";
import {
  assignShift,
  deleteShiftFromUser,
  getActiveShiftsByUserId,
  getEmployeeById,
} from "../api/employeeApi";
import { useDisclosure } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";
import { getWeeklyShifts } from "../api/weeklyShiftApi";
import {
  deleteNotification,
  shiftAssignSuccessNotif,
  shiftConflictErrorNotif,
} from "../utils/useNotification";
import dayjs from "dayjs";
import TableDeleteButton from "../components/common/Buttons/TableDeleteButton";
import EmployeeDetailForm from "../components/EmployeeDetail/EmployeeDetail";
import SaveIcon from "../components/common/Icons/SaveIcon";
import { useForm } from "@mantine/form";
import {
  combineValidators,
  validateMaxLength,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../utils/validations";
import EmployeeShiftDetails from "../components/EmployeeDetail/EmployeeShiftDetails";
import CustomDatePicker from "../components/common/Inputs/CustomDatePicker";
import CustomSelect from "../components/common/Inputs/CustomSelect";
import {
  createWorkPermit,
  getWorkPermitsForEmployee,
} from "../api/workPermitApi";
import { start } from "repl";
import WorkPermitsTable from "../components/EmployeeDetail/WorkPermitsTable";
import DocumentUpload from "../components/common/Inputs/DocumentUpload";
import DocumentsTable from "../components/EmployeeDetail/DocumentsTable";
import { useAuth } from "../context/AuthContext";

export type Employee = {
  _id: string;
  name: string;
  phone: string;
  companyId: string;
  isDeleted: boolean;
  department: string;
  position: string;
  shifts: any;
  workPermits: any;
  documents: any;
};

const EmployeeDetail = () => {
  const { id } = useParams();
  const { company } = useAuth();

  const [activeTab, setActiveTab] = useState<any>("entry-exit");
  const [activeShifts, setActiveShifts] = useState<any>([]);

  const [employee, setEmployee] = useState<Employee>();
  const [
    openedShiftDetailModal,
    { open: openShiftDetailModal, close: closeShiftDetailModal },
  ] = useDisclosure(false);
  const [shiftsSelectData, setShiftSelectData] = useState<any>();
  const [selectedShift, setSelectedShift] = useState<any>();
  const [shiftStartTime, setShiftStartTime] = useState<any>(null);
  const [shiftEndTime, setShiftEndTime] = useState<any>(null);

  const getEmployeeDetails = async () => {
    try {
      const data: any = await getEmployeeById(`${id}`);
      setEmployee(data?.data?.response);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getShifts = async () => {
    try {
      const { data }: any = await getWeeklyShifts({});
      setShiftSelectData(
        data.data.map((e: any) => ({
          label: e.name,
          value: e._id,
          days: e.days,
        }))
      );
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };
  const getActiveShifts = async () => {
    try {
      const { data }: any = await getActiveShiftsByUserId({ userId: id });
      setActiveShifts(data.data.shifts);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const addNewShift = async () => {
    try {
      const { data, response }: any = await assignShift({
        shiftId: [`${selectedShift?.value}`],
        startTime: shiftStartTime,
        selectedIds: [`${id}`],
        shiftEndTime: shiftEndTime,
      });
      if (data?.success) {
        shiftAssignSuccessNotif();
        getActiveShifts();
        setShiftEndTime(null);
        setShiftStartTime(null);
      }
      if (response?.data?.success === false) {
        shiftConflictErrorNotif();
      }
    } catch (error) {
      console.error("Error submitting shift assignment:", error);
    }
  };

  useEffect(() => {
    getEmployeeDetails();
    getActiveShifts();
  }, []);

  useEffect(() => {
    if (openedShiftDetailModal) {
      getShifts();
      setShiftEndTime(null);
      setShiftStartTime(null);
    }
  }, [openedShiftDetailModal]);

  const handleShiftChange = (value: string | null) => {
    const selectedShift = shiftsSelectData.find(
      (shift: any) => shift.value === value
    );
    setSelectedShift(
      selectedShift
        ? {
            value: selectedShift.value,
            label: selectedShift.label,
            days: selectedShift.days,
          }
        : null
    );
  };

  const deleteShift = async ({ shiftId }: any) => {
    try {
      const { data }: any = await deleteShiftFromUser({
        userId: [id],
        userShiftId: [shiftId],
      });
      if (data?.messages[0]?.success) {
        deleteNotification(data?.messages[0]?.success);
        getActiveShifts();
      }
    } catch (error) {}
  };

  const buttonRef = useRef<any>();
  const handleClick = () => {
    buttonRef.current.click();
  };

  const form = useForm({
    initialValues: {
      name: "",
      phone: "",
      department: "",
      isActive: "true",
      position: "",
    },
    validate: {
      name: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11),
        validateMaxLength(11)
      ),
      department: validateRequired,
      isActive: validateRequired,
      position: validateRequired,
    },
  });

  return (
    <Container>
      <PageHeader
        pageTitle={"Çalışan Detayları"}
        isButtoned={activeTab === "detail" ? true : false}
        buttonText="Güncelle"
        buttonOnClick={handleClick}
        buttonIcon={<SaveIcon size={24} />}
        isButtonDisabled={!form.isDirty()}
      />

      {false ? (
        <div>Loading</div>
      ) : (
        <>
          <Tabs value={activeTab} onChange={setActiveTab} keepMounted={true}>
            <Tabs.List mb={20} w={"max-content"}>
              {/* <StyledTabsTab value="detail">Personel Bilgileri</StyledTabsTab> */}
              <StyledTabsTab value="entry-exit">
                Giriş/Çıkış Detayları
              </StyledTabsTab>
              <StyledTabsTab value="work-permits">İzinler</StyledTabsTab>
              {company?.modules?.isDocumentUploadAvailable && (
                <StyledTabsTab value="documents">Özlük Belgeleri</StyledTabsTab>
              )}

              {/* <StyledTabsTab value="shift-detail">
                Vardiya Detayları
              </StyledTabsTab> */}
            </Tabs.List>
            {/* <Tabs.Panel value="detail">
              <EmployeeDetailForm
                employee={employee}
                buttonRef={buttonRef}
                form={form}
              />
            </Tabs.Panel> */}
            <Tabs.Panel value="entry-exit">
              <EmployeeDetailTable />
            </Tabs.Panel>
            <Tabs.Panel value="work-permits">
              <WorkPermitsTable />
            </Tabs.Panel>
            {company?.modules?.isDocumentUploadAvailable && (
              <Tabs.Panel value="documents">
                <DocumentsTable
                  reFetchData={getEmployeeDetails}
                  documents={employee?.documents}
                />
              </Tabs.Panel>
            )}
            {/* <Tabs.Panel value="shift-detail">
              <EmployeeShiftDetails
                activeShifts={activeShifts}
                addNewShift={addNewShift}
                deleteShift={deleteShift}
                handleShiftChange={handleShiftChange}
                openShiftDetailModal={openShiftDetailModal}
              />
            </Tabs.Panel> */}
          </Tabs>
        </>
      )}
      <Modal
        centered
        size={"lxl"}
        opened={openedShiftDetailModal}
        onClose={closeShiftDetailModal}
        title="Vardiya Ekle/Çıkart"
      >
        <Flex direction={"column"} gap={50}>
          <Flex align={"end"} gap={20}>
            <CustomSelect
              w={220}
              h={44}
              label="Vardiya Adı"
              placeholder="Vardiya Seçiniz"
              data={shiftsSelectData}
              onChange={handleShiftChange}
            />
            <CustomDatePicker
              w={220}
              label="Başlangıç Tarihi"
              placeholder="Tarih Seçiniz"
              value={shiftStartTime}
              onChange={setShiftStartTime}
            />
            <CustomDatePicker
              disabled={!shiftStartTime}
              w={220}
              minDate={shiftStartTime}
              label="Bitiş Tarihi (Opsiyonel)"
              placeholder="Tarih Seçiniz"
              value={shiftEndTime}
              onChange={setShiftEndTime}
            />
            <Button color="#5c26ff" onClick={() => addNewShift()}>
              Ekle
            </Button>
          </Flex>
          {activeShifts?.length > 0 && (
            <Flex>
              <div
                style={{
                  overflowY: "auto",
                  minHeight: "max-content",
                  width: "100%",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                }}
              >
                <Table>
                  <Table.Thead
                    style={{
                      background: "#EEF2FF",
                    }}
                  >
                    <Table.Tr>
                      <StyledTableTh>Vardiya Adı</StyledTableTh>
                      <StyledTableTh>Vardiya Başlangıç Tarihi</StyledTableTh>
                      <StyledTableTh>Vardiya Bitiş Tarihi</StyledTableTh>
                      <StyledTableTh></StyledTableTh>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {activeShifts?.map((shift: any) => (
                      <Table.Tr
                        style={{
                          color: "#111827",
                          fontSize: "16px",
                          fontWeight: "400",
                        }}
                        key={shift._id}
                      >
                        <Table.Td>{shift.shiftName}</Table.Td>
                        <Table.Td>
                          {dayjs(shift.effectiveDate).format("DD/MM/YY")}
                        </Table.Td>
                        <Table.Td>
                          {shift?.shiftEndTime
                            ? dayjs(shift.shiftEndTime).format("DD/MM/YY")
                            : "Yok"}
                        </Table.Td>
                        <Table.Td>
                          <TableDeleteButton
                            onClick={() => deleteShift({ shiftId: shift._id })}
                          />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            </Flex>
          )}
        </Flex>
      </Modal>
    </Container>
  );
};

export default EmployeeDetail;

const StyledTabsTab = styled(Tabs.Tab)`
  color: #9ca3af;
  &[aria-selected="true"] {
    color: #5c26ff;
    border-color: #5c26ff;
    border-bottom-width: 3px;
  }
`;

const StyledTableTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;
